import { AppliedFilterSchema, FilterOperator, isFilterOperator } from '@shared/dataview/core/dataview-types';

export const VIEW_PARAM = 'view';
export const FILTER_PARAM_PREFIX = 'filter_';

const RANGE_SEPARATOR = '_';
const LIST_SEPARATOR = ';';
const TYPE_SEPARATOR = '|';

const LIST_OPERATORS: ReadonlySet<FilterOperator> = new Set(['in', 'nin', 'isOneOf', 'nisOneOf']);

export type UrlParams = Record<string, unknown>;

export type DecodeFiltersResult = {
  filters: AppliedFilterSchema[];
  errors: Array<{ param: string; reason: string }>;
};

export function decodeFilterParams(params: UrlParams): DecodeFiltersResult {
  const filters: AppliedFilterSchema[] = [];
  const errors: DecodeFiltersResult['errors'] = [];

  for (const param of Object.keys(params).filter(key => key.startsWith(FILTER_PARAM_PREFIX))) {
    const id = param.slice(FILTER_PARAM_PREFIX.length);
    const raw = params[param];
    if (typeof raw !== 'string') {
      errors.push({ param, reason: 'Expected a string value' });
      continue;
    }

    const [rawType, ...rest] = raw.split(TYPE_SEPARATOR);
    const rawValue = rest.join(TYPE_SEPARATOR);
    const type = isFilterOperator(rawType) ? rawType : undefined;

    let value: unknown = rawValue;

    if (type === 'datetimeRange') {
      const bounds = rawValue.split(RANGE_SEPARATOR);
      if (bounds.length <= 1) {
        errors.push({
          param,
          reason: `Expected a value pair (from${RANGE_SEPARATOR}to) for a datetimeRange, got "${rawValue}"`,
        });
        continue;
      }
      value = { from: bounds[0], to: bounds[1] };
    }

    if (type && LIST_OPERATORS.has(type)) {
      value = rawValue.split(LIST_SEPARATOR);
    }

    filters.push({ id, value, type });
  }

  return { filters, errors };
}

export function encodeFilterParams(filters: AppliedFilterSchema[]): Record<string, string> {
  const params: Record<string, string> = {};

  for (const filter of filters) {
    const encoded = encodeFilterValue(filter);
    if (encoded === undefined) continue;
    params[`${FILTER_PARAM_PREFIX}${filter.id}`] = encoded;
  }

  return params;
}

function encodeFilterValue(filter: AppliedFilterSchema): string | undefined {
  const { type, value } = filter;
  const prefix = type ? `${type}${TYPE_SEPARATOR}` : TYPE_SEPARATOR;

  if (type === 'datetimeRange') {
    const range = value as { from?: unknown; to?: unknown } | null;
    if (!range || typeof range !== 'object' || range.from === undefined || range.to === undefined) return undefined;
    return `${prefix}${range.from}${RANGE_SEPARATOR}${range.to}`;
  }

  if (Array.isArray(value)) {
    return type && LIST_OPERATORS.has(type) ? `${prefix}${value.join(LIST_SEPARATOR)}` : undefined;
  }

  if (value === null || value === undefined || typeof value === 'object') return undefined;

  return `${prefix}${value}`;
}

export function buildViewParams(viewId: string, currentParams: UrlParams): Record<string, string | null> {
  const patch: Record<string, string | null> = { [VIEW_PARAM]: viewId };

  for (const key of Object.keys(currentParams)) {
    if (key.startsWith(FILTER_PARAM_PREFIX)) patch[key] = null;
  }

  return patch;
}
